import React, { useState, useEffect, useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { green, red, amber, blue } from '@mui/material/colors';
import { ApiInstance } from '../../../api/api';
import {
  DhcpCreateDialogState,
  BaseDhcpReservationCreateRequestType,
  DhcpReservationCreationResponse,
  DhcpReservationConflictResolutionRequestType,
  ConflictedDhcpReservationDataType,
  FailedDhcpResolution,
  SuccessfulDhcpResolution,
} from '../../../types/types';
import IconButton from '@mui/material/IconButton';
import AddDhcpReservationForm from './forms/AddDhcpReservationForm';
import DialogContentLoading from '../common/DialogContentLoading';
import DialogContentSuccess from '../common/DialogContentSuccess';
import DialogContentError from '../common/DialogContentError';
import ReservationConflictForm from './forms/ReservationConflictForm';
import dialogStyles from '../common/dialogStyles';
import { parseApiError } from '../../../utils/apiErrorHandler';
import useApplyFilterParam from '../../../hooks/useApplyFilterParam';
import PartialConflictResolutionResult from './forms/PartialConflictResolutionResult';

interface AddDhcpReservationDialogProps {
  open: boolean;
  onClose: () => void;
  ipAddress: string | undefined;
  deviceType: string | null;
  region: string | null;
  buildingCode: string | null;
  unit: string;
  unitName: string | null;
  dhcpInterface: string;
  dhcpReservationIp: string;
  dhcpReservationMac: string;
  currentDhcpReservationDescription: string;
}

function useSelectedItem() {
  const [description, setDescription] = useState<string>('');
  const [touched, setTouched] = useState<{
    description: boolean;
  }>({
    description: false,
  });
  const allowedInputCharacters: RegExp = /^[a-zA-Z0-9-_]*$/;
  const isDescriptionValid: boolean =
    description.trim().length > 0 && allowedInputCharacters.test(description);
  const markDescriptionTouched = useCallback(
    () => setTouched((t) => ({ ...t, description: true })),
    []
  );

  return {
    description,
    setDescription,
    allowedInputCharacters,
    isDescriptionValid,
    touched,
    markDescriptionTouched,
    setTouched,
  };
}

const AddDhcpReservationDialog: React.FC<AddDhcpReservationDialogProps> = ({
  open,
  onClose,
  ipAddress,
  deviceType,
  region,
  buildingCode,
  unit,
  unitName,
  dhcpInterface,
  dhcpReservationIp,
  dhcpReservationMac,
  currentDhcpReservationDescription,
}) => {
  const {
    description,
    setDescription,
    allowedInputCharacters,
    isDescriptionValid,
    touched,
    markDescriptionTouched,
    setTouched,
  } = useSelectedItem();
  const isConfirmDisabled = !isDescriptionValid;
  const descriptionRef = useRef<HTMLInputElement | null>(null);

  const resetForm = () => {
    setDescription('');
    setTouched({ description: false });
  };

  const DEFAULT_LOADING_TEXT = 'Please wait while we process your request...';
  const DEFAULT_ERROR_TEXT = 'Something went wrong. Please try again later.';

  const titleMap: Record<DhcpCreateDialogState, string> = {
    default: 'New DHCP Reservation',
    loading: 'New DHCP Reservation',
    success: 'New DHCP Reservation',
    error: 'New DHCP Reservation',
    conflict_detected: 'DHCP Reservation Conflict',
    resolution_pending: 'DHCP Reservation Conflict',
    resolution_partial: 'DHCP Reservation Conflict',
    resolution_success: 'DHCP Reservation Conflict',
    resolution_failure: 'DHCP Reservation Conflict',
  };

  const backgroundColorMap: Record<DhcpCreateDialogState, string> = {
    default: amber[800],
    loading: blue[600],
    success: green[500],
    error: red[500],
    conflict_detected: red[500],
    resolution_pending: blue[600],
    resolution_partial: amber[800],
    resolution_success: green[500],
    resolution_failure: red[500],
  };

  const [dialogState, setDialogState] = useState<DhcpCreateDialogState>('default');
  const [loadingText, setLoadingText] = useState(DEFAULT_LOADING_TEXT);
  const [errorText, setErrorText] = useState(DEFAULT_ERROR_TEXT);
  const [reloadOnClose, setReloadOnClose] = useState<Boolean>(false);
  const [actionResult, setActionResult] = React.useState<DhcpReservationCreationResponse | null>(
    null
  );
  const [conflictedReservation, setConflictedReservation] =
    React.useState<ConflictedDhcpReservationDataType | null>(null);
  const [successfulResolutions, setSuccessfulResolutions] = useState<SuccessfulDhcpResolution[]>(
    []
  );
  const [failedResolutions, setFailedResolutions] = useState<FailedDhcpResolution[]>([]);

  const applyFilterAfterCreate = useApplyFilterParam('mac_address', actionResult?.data.mac ?? '');

  const resolveBatchDialogState = (
    succeeded: SuccessfulDhcpResolution[],
    failed: FailedDhcpResolution[]
  ): DhcpCreateDialogState => {
    const hasSuccesses = succeeded.length > 0;
    const hasFailures = failed.length > 0;

    if (hasSuccesses && !hasFailures) return 'resolution_success';
    if (!hasSuccesses && hasFailures) return 'resolution_failure';
    if (hasSuccesses && hasFailures) return 'resolution_partial';
    return 'resolution_failure';
  };

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setDialogState('default');
        setLoadingText(DEFAULT_LOADING_TEXT);
        setErrorText(DEFAULT_ERROR_TEXT);
        setConflictedReservation(null);
        setSuccessfulResolutions([]);
        setFailedResolutions([]);
        setReloadOnClose(false);
      }, 100);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!isConfirmDisabled) {
      setDialogState('loading');
      setLoadingText(DEFAULT_LOADING_TEXT);

      try {
        const payload: BaseDhcpReservationCreateRequestType = {
          device_ip: ipAddress,
          device_vendor: deviceType,
          region: region,
          building_code: buildingCode,
          unit: unit,
          unit_name: unitName,
          reservation_data: {
            interface: dhcpInterface,
            dhcp_reservation_ip: dhcpReservationIp,
            dhcp_reservation_mac: dhcpReservationMac,
            dhcp_reservation_description: currentDhcpReservationDescription,
            new_dhcp_reservation_description: description.toLowerCase(),
          },
        };

        const response = await ApiInstance.createDhcpReservation(payload);

        setDialogState('success');
        setActionResult(response);
        setReloadOnClose(true);
      } catch (error: any) {
        setErrorText(parseApiError(error));
        setReloadOnClose(false);

        if (error.response?.status === 409 && error.response?.data?.conflicted_reservation) {
          setConflictedReservation(error.response.data.conflicted_reservation);
          setDialogState('conflict_detected');
        } else {
          setDialogState('error');
        }
      }
    } else {
      if (!isDescriptionValid && descriptionRef.current) {
        descriptionRef.current.focus();
      }
    }
  };

  const handleResolve = async () => {
    setDialogState('resolution_pending');

    if (conflictedReservation) {
      try {
        const payload: DhcpReservationConflictResolutionRequestType = {
          device_ip: ipAddress,
          device_vendor: deviceType,
          region: region,
          building_code: buildingCode,
          unit: unit,
          unit_name: unitName,
          reservation_data: {
            interface: dhcpInterface,
            dhcp_reservation_ip: dhcpReservationIp,
            dhcp_reservation_mac: dhcpReservationMac,
            dhcp_reservation_description: currentDhcpReservationDescription,
            new_dhcp_reservation_description: description.toLowerCase(),
          },
          conflicted_reservation: {
            ip: conflictedReservation.ip,
            host: conflictedReservation.host,
            mac: conflictedReservation.mac,
          },
        };

        const { succeeded, failed } = await ApiInstance.resolveDhcpReservationConflict(payload);
        const state = resolveBatchDialogState(succeeded, failed);

        setSuccessfulResolutions(succeeded);
        setFailedResolutions(failed);

        setDialogState(state);
        setReloadOnClose(true);
      } catch (error: any) {
        setErrorText(parseApiError(error));
        setReloadOnClose(false);
        setDialogState('resolution_failure');
      }
    } else {
      setDialogState('resolution_failure');
    }
  };

  const handleClose = () => {
    if (reloadOnClose) {
      resetForm();
      applyFilterAfterCreate();
    } else {
      resetForm();
      onClose();
    }
  };

  const getDialogTitle = () => titleMap[dialogState];
  const getTitleBackgroundColor = () => backgroundColorMap[dialogState];

  const getDialogContent = () => {
    switch (dialogState) {
      case 'loading':
      case 'resolution_pending':
        return <DialogContentLoading loadingText={loadingText} />;
      case 'success':
      case 'resolution_success':
        return <DialogContentSuccess waitBlock={false} />;
      case 'error':
      case 'resolution_failure':
        return <DialogContentError errorText={errorText} />;
      case 'conflict_detected':
        return (
          <ReservationConflictForm
            conflictedReservation={conflictedReservation}
            errorText={errorText}
          />
        );
      case 'resolution_partial':
        return (
          <PartialConflictResolutionResult
            succeeded={successfulResolutions}
            failed={failedResolutions}
          />
        );
      default:
        return (
          <>
            <AddDhcpReservationForm
              reservationIp={dhcpReservationIp}
              reservationMac={dhcpReservationMac}
              description={description}
              setDescription={setDescription}
              allowedInputCharacters={allowedInputCharacters}
              isDescriptionValid={isDescriptionValid}
              touched={touched}
              markDescriptionTouched={markDescriptionTouched}
            />
          </>
        );
    }
  };

  const showCloseIcon = !['loading', 'resolution_pending'].includes(dialogState);
  const showDialogActions = dialogState === 'default';
  const showResolveConflictButton = dialogState === 'conflict_detected' && conflictedReservation;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={dialogStyles.dialog}
      sx={dialogStyles.dialogPaper}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-content"
    >
      <DialogTitle
        id="dialog-title"
        sx={{
          ...dialogStyles.dialogTitle,
          backgroundColor: getTitleBackgroundColor(),
        }}
      >
        {getDialogTitle()}
        {showCloseIcon && (
          <IconButton aria-label="close" onClick={handleClose} sx={dialogStyles.dialogCloseButton}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent id="dialog-content" sx={dialogStyles.dialogContent}>
        {getDialogContent()}
      </DialogContent>
      {showDialogActions && (
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button
            onClick={handleClose}
            color="secondary"
            variant="contained"
            sx={dialogStyles.cancelButtonStyles}
          >
            <CloseIcon sx={dialogStyles.buttonIconSpacing} />
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            sx={dialogStyles.confirmButtonStyles}
          >
            <CheckIcon sx={dialogStyles.buttonIconSpacing} />
            Confirm
          </Button>
        </DialogActions>
      )}
      {showResolveConflictButton && (
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button
            onClick={handleResolve}
            color="primary"
            variant="contained"
            sx={dialogStyles.confirmButtonStyles}
          >
            <CheckIcon sx={dialogStyles.buttonIconSpacing} />
            Resolve Conflict
          </Button>
          <Button
            onClick={handleClose}
            color="secondary"
            variant="contained"
            sx={dialogStyles.cancelButtonStyles}
          >
            <CloseIcon sx={dialogStyles.buttonIconSpacing} />
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AddDhcpReservationDialog;
